import { InternalEventType } from '../../../domain/enums/internal-event-type';
import { Referrer } from '../../../../interfaces/enums/referrer';
import globalStore from '@/store/global';
import globals from '@/utils/globals';
import { GoogleLoginStatus, GSIClient, GSIDismissedReason, GSIId, GSINotification } from './types';
import { GoogleButtonSize } from '@/domain/enums/google-button-size';

declare const google: GSIClient;
const GOOGLE_RETRY_LIMIT = 4;
const GOOGLE_RETRY_DELAY = 200;
const GOOGLE_RETRY_DELAY_MAX = 5000;

export class GoogleClient {
	private googleLoadFailCount: number = 0;
	private loadInitializeSDKPromise: Promise<void> | null = null;
	private sdkLoaded: boolean = false;
	private sdkFailed: boolean = false;
	private gsiScript: HTMLScriptElement | null = null;
	constructor(private clientId: string) {}

	private awaitedLoad(): Promise<void> {
		if (this.loadInitializeSDKPromise) return this.loadInitializeSDKPromise;

		this.loadInitializeSDKPromise = this.initializeSDK();
		return this.loadInitializeSDKPromise;
	}

	public async showYolo(): Promise<string> {
		await this.awaitedLoad();

		return new Promise((resolve) => {
			this.initializeYolo(resolve);

			if (this.shouldSkipSigninPrompt) return;
			if (!this.googleInstance) return;

			this.googleInstance.prompt((notification) => this.emitYoloStatus(notification));
		});
	}

	public async initializeSDK(): Promise<void> {
		if (this.sdkLoaded || this.sdkFailed) return;
		if (this.loadInitializeSDKPromise) return this.loadInitializeSDKPromise;

		this.loadInitializeSDKPromise = new Promise((resolve, reject) => this.tryLoadSDK(resolve, reject));
		return this.loadInitializeSDKPromise;
	}

	private tryLoadSDK(resolve: () => void, reject: (reason?: any) => void): void {
		if (this.isGoogleSDKAlreadyLoaded()) {
			this.handleScriptLoad(resolve);
			return;
		}

		this.cleanupPreviousScript();
		this.injectGSIScript();

		this.gsiScript!.onload = (): void => this.handleScriptLoad(resolve);
		this.gsiScript!.onerror = (): void => {
			try {
				this.handleScriptError(resolve, reject);
			} catch {
				//ignored so it doesn't fire on Rollbar
			}
		};
	}

	private cleanupPreviousScript(): void {
		if (!this.gsiScript) return;

		this.gsiScript.onload = null;
		this.gsiScript.onerror = null;
		document.head.removeChild(this.gsiScript);
		this.gsiScript = null;
	}

	private injectGSIScript(): void {
		this.gsiScript = document.createElement('script');
		this.gsiScript.src = 'https://accounts.google.com/gsi/client';
		this.gsiScript.async = false;
		document.head.appendChild(this.gsiScript);
	}

	private handleScriptLoad(resolve: () => void): void {
		if (this.sdkLoaded || this.sdkFailed) return;

		this.sdkLoaded = true;
		resolve();
		this.loadInitializeSDKPromise = null;
	}

	private handleScriptError(resolve: () => void, reject: (reason?: any) => void): void {
		if (this.sdkLoaded || this.sdkFailed) return;

		this.googleLoadFailCount++;

		if (this.googleLoadFailCount > GOOGLE_RETRY_LIMIT) {
			this.sdkFailed = true;

			reject(new Error('Google SDK failed to load after max retries.'));
			this.loadInitializeSDKPromise = null;
			return;
		}

		const delayMs = Math.min(GOOGLE_RETRY_DELAY * (2 ** this.googleLoadFailCount), GOOGLE_RETRY_DELAY_MAX);
		setTimeout(() => this.tryLoadSDK(resolve, reject), delayMs);
	}

	public async renderGoogleButton(options: { element: Element; template: string, gButtonSize?: GoogleButtonSize }, referrer: Referrer | null): Promise<void> {
		await this.awaitedLoad();

		if (!this.googleInstance) return;
		if (!options.element) return;
		if (!options.element.parentElement) return;

		this.googleInstance.renderButton(options.element as HTMLElement, {
			type: options.template === 'Small' ? 'icon' : 'standard',
			shape: options.template === 'Small' ? 'square' : 'rectangular',
			text: 'signin_with',
			theme: 'filled_blue',
			size: options.gButtonSize || GoogleButtonSize.MEDIUM,
			width: this.getSiblingButtonWidth(options.element),
			logo_alignment: 'left',
			locale: 'en',
			click_listener: () => {
				globalStore.mutations.setReferrerGoogleAuth(referrer);
			},
		});
	}

	private get isFedCMSupported(): boolean {
		return (
			!!window.navigator &&
			!!window.navigator.credentials &&
			typeof window.navigator.credentials.get === 'function' &&
			this.isChromiumBased()
		);
	}

	private isChromiumBased(): boolean {
		try {
			const userAgent = navigator.userAgent.toLowerCase();
			const isChromium = userAgent.includes('chrome') || userAgent.includes('chromium');
			const chromeVersion = userAgent.match(/chrome\/(\d+)/);
			const isAbove116 = chromeVersion ? parseInt(chromeVersion[1], 10) >= 116 : false;
			const isIos = /iphone|ipad|ipod/.test(userAgent);

			return isChromium && isAbove116 && !isIos;
		} catch {
			return false;
		}
	}

	private emitYoloStatus(notification: GSINotification): void {
		const status: GoogleLoginStatus = GoogleClient.getGoogleLoginStatus(notification);

		switch (status) {
		case GoogleLoginStatus.SKIPPED:
			globals.eventBus.emit(InternalEventType.YOLO_NOT_DISPLAYED);
			break;
		case GoogleLoginStatus.LEGACY_DISPLAYED:
			globals.eventBus.emit(InternalEventType.YOLO_DISPLAYED);
			break;
		case GoogleLoginStatus.LOGGED_IN:
			globalStore.mutations.setReferrerGoogleAuth(Referrer.GOOGLE_YOLO);
			globals.eventBus.emit(InternalEventType.YOLO_DISMISSED);
			break;
		}
	}

	private getSiblingButtonWidth(element: Element): number {
		const siblingButton: Element | null = element.parentNode?.querySelector('.Provider') ?? null;

		if (!siblingButton) return 400;

		return Math.round(siblingButton.getBoundingClientRect().width);
	}

	private initializeYolo(callback: (credentials) => void, parentId?: string): void {
		if (!this.googleInstance) return;

		this.googleInstance.initialize({
			client_id: this.clientId,
			callback: (response) => callback(response.credential),
			context: 'signin',
			ux_mode: 'popup',
			auto_select: false,
			prompt_parent_id: parentId,
			cancel_on_tap_outside: false,
			itp_support: true,
			use_fedcm_for_prompt: this.isFedCMSupported,
			use_fedcm_for_button: this.isFedCMSupported,
		});

		globals.eventBus.emit(InternalEventType.YOLO_INITIALIZED);
	}

	private get googleInstance(): GSIId | null {
		if (typeof google === 'undefined' || !google.accounts || !google.accounts.id) return null;

		return google.accounts.id;
	}

	private get shouldSkipSigninPrompt(): boolean {
		return !!window['OpenIdFlow'];
	}

	private static getGoogleLoginStatus(notification: GSINotification): GoogleLoginStatus {
		if (notification.isDisplayed()) return GoogleLoginStatus.LEGACY_DISPLAYED;

		if (notification.isSkippedMoment()) return GoogleLoginStatus.SKIPPED;

		if (notification.getDismissedReason() === GSIDismissedReason.CREDENTIAL_RETURNED) return GoogleLoginStatus.LOGGED_IN;

		return GoogleLoginStatus.SKIPPED;
	}

	private isGoogleSDKAlreadyLoaded(): boolean {
		try {
			return typeof google !== 'undefined' && !!google.accounts && !!google.accounts.id;
		} catch {
			return false;
		}
	}
}
